import { ref } from 'vue'

export interface OperatorScriptData {
  header: string
  headerIcon: string
  body: string
  acceptButtonText: string
  declineButtonText: string
  okButtonClass: string
  cancelButtonClass: string
  showOk: boolean
  showCancel: boolean
  modalSize: string
  centeredVertically: boolean
}

export interface OperatorScriptResult {
  success: boolean
  hasOp: boolean
  text: string
}

function parseHtml(html: string): OperatorScriptData | null {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const modal = doc.getElementById('operatorScript')
  if (!modal) return null

  const titleEl = modal.querySelector('.modal-title')
  const iconEl = titleEl?.querySelector('em')
  const headerIcon = iconEl?.className ?? ''
  const header = titleEl?.textContent?.trim() ?? ''
  const body = modal.querySelector('.modal-body')?.innerHTML?.trim() ?? ''
  const okButton = modal.querySelector('#osOkButton') as HTMLElement | null
  const cancelButton = modal.querySelector('#osCancelButton') as HTMLElement | null
  const dialog = modal.querySelector('.modal-dialog') as HTMLElement | null
  const dialogClasses = dialog?.className ?? ''

  return {
    header,
    headerIcon,
    body,
    acceptButtonText: okButton?.textContent?.trim() ?? 'Continue',
    declineButtonText: cancelButton?.textContent?.trim() ?? 'Cancel',
    okButtonClass: okButton?.className ?? 'btn btn-primary',
    cancelButtonClass: cancelButton?.className ?? 'btn btn-secondary',
    showOk: !!okButton,
    showCancel: !!cancelButton,
    modalSize: dialogClasses.match(/modal-(sm|md|lg|xl|fullscreen)/)?.[0] ?? '',
    centeredVertically: dialogClasses.includes('modal-dialog-centered'),
  }
}

export function useOperatorScript() {
  const visible = ref(false)
  const script = ref<OperatorScriptData | null>(null)

  let resolvePromise: ((result: OperatorScriptResult) => void) | null = null

  async function fetchScript(
    object: string,
    action: string,
    subobject: string | null,
    event: string,
    parameters?: any
  ): Promise<OperatorScriptData | null> {
    let url = `/OperatorScripts/${object}/${action}/${event}`
    if (subobject) {
      url = `/OperatorScripts/withsubobject/${object}/${subobject}/${action}/${event}`
    }

    const csrfToken = (window as any).token ?? ''

    const response = await fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN-VERIFICATION': csrfToken,
      },
      body: parameters ? JSON.stringify(parameters) : null,
      credentials: 'include',
    })

    if (response.status !== 200) {
      return null
    }

    const html = await response.text()
    return parseHtml(html)
  }

  function show(
    object: string,
    action: string,
    subobject: string | null,
    event: string,
    parameters?: any,
    callback?: (evt: OperatorScriptResult) => void
  ): Promise<OperatorScriptResult> {
    return new Promise(async (resolve) => {
      try {
        const data = await fetchScript(object, action, subobject, event, parameters)

        if (!data) {
          const result: OperatorScriptResult = { success: true, hasOp: false, text: 'No operator script found' }
          callback?.(result)
          resolve(result)
          return
        }

        script.value = data
        visible.value = true
        resolvePromise = (result) => {
          callback?.(result)
          resolve(result)
        }
      } catch {
        const result: OperatorScriptResult = { success: true, hasOp: false, text: 'Failed to load operator script' }
        callback?.(result)
        resolve(result)
      }
    })
  }

  function accept() {
    visible.value = false
    resolvePromise?.({ success: true, hasOp: true, text: 'Operator script loaded - Accepted' })
    resolvePromise = null
    script.value = null
  }

  function decline() {
    visible.value = false
    resolvePromise?.({ success: false, hasOp: true, text: 'Operator script loaded - Declined' })
    resolvePromise = null
    script.value = null
  }

  return {
    visible,
    script,
    show,
    accept,
    decline,
  }
}
